import type { InMemoryCacheConfig, Reference } from '@apollo/client';

import type { Extension as FhirExtension } from 'generated/types';

type RecordArray = {
  [key: string]: any;
  data: Reference[];
};

type RecordType = RecordArray | undefined;

type Extension = Omit<FhirExtension, 'valueAttachment'> & {
  valueAttachment: Reference | null;
};

function mergeRecords(existing: RecordType, incoming: RecordType) {
  let records: RecordArray = { data: [] };
  if (existing) {
    records = {
      ...records,
      ...existing,
      data: [...records.data, ...existing.data],
    };
  }
  if (incoming) {
    records = {
      ...records,
      ...incoming,
      data: records.data.concat(
        incoming.data.filter(
          (n) =>
            records.data.find(
              // eslint-disable-next-line
              (record) => record.__ref === n.__ref
            ) === undefined
        )
      ),
    };
  }
  return records;
}

function mergeAttachmentExtensions(
  existing: Extension[] | undefined,
  incoming: Extension[] | undefined
) {
  if (!incoming) return existing ?? [];
  if (!existing) return incoming;

  const hasAttachment = (ext: Extension) => Boolean(ext.valueAttachment);

  // Attachment extensions: keep existing, add new incoming (dedupe by ref)
  const existingAttachments = existing.filter(hasAttachment);
  const mergedAttachments = [
    ...existingAttachments,
    ...incoming
      .filter(hasAttachment)
      .filter(
        (a) =>
          !existingAttachments.find(
            (b) => b.valueAttachment?.__ref === a.valueAttachment?.__ref
          )
      ),
  ];

  // Non-attachment extensions: incoming is authoritative
  const incomingNonAttachments = incoming.filter((e) => !hasAttachment(e));

  return [...mergedAttachments, ...incomingNonAttachments];
}

function mergeAttachments(
  existing: Reference[] | undefined,
  incoming: Reference[] | undefined
) {
  // Preserve cached attachments when the server returns an empty array.
  // After postAttachment, the backend's DocumentReference read can briefly
  // return [] before the new reference propagates; without this, that empty
  // response would clobber the value usePostAttachment just wrote via
  // cache.writeFragment. In-session deletions use cache.evict on the
  // Attachment entity, so they bypass this merge and still propagate.
  if (!incoming?.length && existing?.length) return existing;
  return incoming ?? [];
}

const fhirRecordPolicy = {
  keyFields: ['id'],
  fields: {
    extension: {
      merge(
        existing: Extension[] | undefined,
        incoming: Extension[] | undefined
      ) {
        return mergeAttachmentExtensions(existing, incoming);
      },
    },
    attachments: {
      merge: mergeAttachments,
    },
  },
};

const config: InMemoryCacheConfig = {
  possibleTypes: {
    ResourceAssociationUnion: [
      'AllergyModel',
      'AppointmentModel',
      'ProcedureModel',
      'EncounterModel',
      'ImmunizationModel',
      'ConditionModel',
      'MedicationStatementModel',
      'DiagnosticReportModel',
      'ObservationModel',
      'TaskModel',
      'FamilyMemberHistoryModel',
      'CoverageModel',
    ],
    ViewedStatusResourcesUnion: [
      'AllergyModel',
      'AppointmentModel',
      'ProcedureModel',
      'EncounterModel',
      'ImmunizationModel',
      'ConditionModel',
      'MedicationStatementModel',
      'DiagnosticReportModel',
      'ObservationModel',
      'FamilyMemberHistoryModel',
    ],
    NotesResourcesUnion: [
      'AllergyModel',
      'AppointmentModel',
      'ProcedureModel',
      'EncounterModel',
      'ImmunizationModel',
      'ConditionModel',
      'MedicationStatementModel',
      'DiagnosticReportModel',
      'ObservationModel',
      'FamilyMemberHistoryModel',
    ],
    SharingAssociationUnion: [
      'AllergyModel',
      'AppointmentModel',
      'ConditionModel',
      'ConsentModel',
      'CoverageModel',
      'DiagnosticReportModel',
      'EncounterModel',
      'FamilyMemberHistoryModel',
      'ImmunizationModel',
      'MedicationStatementModel',
      'ObservationModel',
      'PatientModel',
      'ProcedureModel',
    ],
  },
  typePolicies: {
    /**
     * A contained Medication's `id` is resource-local, and the medication[x]
     * migration writes the same `med-rx` for every record. Normalizing on it
     * would collapse every medication in the app onto one cache entity, so these
     * stay embedded in their parent MedicationStatement. See
     * `normalization.spec.ts`.
     */
    MedicationModel: {
      keyFields: false,
    },
    OperationModel: {
      keyFields: ['operation'],
    },
    PermissionModel: {
      keyFields: ['permission_id'],
    },
    PolicyPermissionModel: {
      keyFields: ['permission_id'],
    },
    PolicyModel: {
      keyFields: ['policy_id'],
    },
    RoleModel: {
      keyFields: ['role_id'],
    },
    RoutineInstanceStatusModel: {
      keyFields: ['instance_id'],
    },
    AccountModel: {
      keyFields: ['account_id'],
    },
    CurationMeta: {
      keyFields: ['resource_name', 'resource_model_id'],
    },
    CuratorTaskModel: {
      keyFields: ['curator_task_id'],
    },
    DocumentModel: {
      keyFields: ['document_id'],
    },
    ScanProfile: {
      keyFields: ['patient_id'],
    },
    StatusModel: {
      keyFields: ['status_id'],
    },
    UserModel: {
      keyFields: ['user_id'],
    },
    UserNotificationPreference: {
      keyFields: ['user_id', 'identifier'],
    },
    UserNotificationConnection: {
      keyFields: ['user_id', 'connected_user_id'],
    },
    VitallPatientModel: {
      keyFields: ['vitall_patient_id'],
    },
    PermissionControlSet: {
      keyFields: ['permission_control_set_id'],
    },
    PermissionControl: {
      keyFields: ['permission_control_id'],
    },
    PlanCreditModel: {
      keyFields: ['id', 'user_id'],
    },
    PatientCareTeamModel: {
      keyFields: ['patient_id', 'category'],
    },
    UserSettings: {
      keyFields: ['user_id'],
    },
    ActivityModel: {
      keyFields: ['summary_id'],
    },
    SharedResourceHistory: {
      keyFields: ['share_id', 'recipient_id'],
    },
    ScanResultModel: {
      keyFields: ['result_id', 'scan_type'],
    },
    AssessmentModel: {
      keyFields: ['assessment_id'],
    },
    ReferenceRangeModel: {
      keyFields: ['reference_range_id'],
    },
    AlertModel: {
      keyFields: ['alert_id'],
    },
    AlertSettingModel: {
      keyFields: ['alert_setting_id', 'vitall_patient_id'],
    },
    AlertRecipientSettingModel: {
      keyFields: ['user_id', 'account_id'],
    },
    ObservationAlertModel: {
      keyFields: ['alert_id', 'observation_id'],
    },
    ContractPlanModel: {
      keyFields: ['account_id', 'plan_id'],
    },
    LicenseModel: {
      keyFields: ['license_id'],
    },
    AccountSettingModel: {
      keyFields: ['account_id'],
    },
    DiscernResultModel: {
      keyFields: ['discern_result_id'],
    },
    DiscernJobModel: {
      keyFields: ['discern_job_id'],
    },
    DiscernDocumentModel: {
      keyFields: ['discern_document_id'],
    },
    AllergyModel: fhirRecordPolicy,
    AppointmentModel: fhirRecordPolicy,
    ConditionModel: fhirRecordPolicy,
    CoverageModel: fhirRecordPolicy,
    DiagnosticReportModel: fhirRecordPolicy,
    EncounterModel: fhirRecordPolicy,
    FamilyMemberHistoryModel: fhirRecordPolicy,
    ImmunizationModel: fhirRecordPolicy,
    MedicationStatementModel: fhirRecordPolicy,
    ProcedureModel: fhirRecordPolicy,
    ObservationModel: fhirRecordPolicy,
    ConsentModel: {
      keyFields: ['id'],
      fields: {
        extension: {
          merge(
            existing: FhirExtension[] | undefined,
            incoming: FhirExtension[] | undefined
          ) {
            // Adding an attachment to a consent record
            if (incoming?.some(({ valueAttachment }) => valueAttachment)) {
              return mergeAttachmentExtensions(
                existing as Extension[],
                incoming as Extension[]
              );
            }
            // Legal directives: Replace old with new
            if (
              incoming?.some(
                ({ valueBoolean }) => typeof valueBoolean === 'boolean'
              )
            ) {
              return incoming;
            }
            // Default
            return [...(existing ?? []), ...(incoming ?? [])];
          },
        },
        attachments: {
          merge: mergeAttachments,
        },
      },
    },
    Query: {
      fields: {
        listDiagnosticReports: {
          keyArgs: false,
          merge(existing: RecordType, incoming: RecordType) {
            mergeRecords(existing, incoming);
          },
        },
        listTerraActivities: {
          keyArgs: ['input', ['vitall_patient_id']],
          merge(
            existing:
              | { cursor: string; hasMore: boolean; data: Reference[] }
              | undefined,
            incoming:
              | { cursor: string; hasMore: boolean; data: Reference[] }
              | undefined
          ) {
            let activities: {
              cursor: string;
              hasMore: boolean;
              data: Reference[];
            } = {
              cursor: '',
              hasMore: false,
              data: [],
            };

            if (existing) {
              activities = {
                cursor: existing.cursor,
                hasMore: existing.hasMore,
                data: [...activities.data, ...existing.data],
              };
            }

            if (incoming) {
              activities = {
                cursor: incoming.cursor,
                hasMore: incoming.hasMore,
                data: [
                  ...activities.data,
                  ...incoming.data.filter(
                    (a) =>
                      activities.data.find(
                        (activity) => activity.__ref === a.__ref
                      ) === undefined
                  ),
                ],
              };
            }
            return activities;
          },
        },
        listAssessments: {
          keyArgs: ['input', ['type', 'vitall_patient_id']],
        },
        listBloodTypeObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listBodyObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listFitnessObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listLaboratoryObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listSleepObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listVitalSignObservations: {
          keyArgs: ['input', ['code', 'patient']],
        },
        listScanResults: {
          keyArgs: ['input', ['scan_type', 'vitall_patient_id']],
          merge(
            existing:
              | { cursor: string; hasMore: boolean; data: Reference[] }
              | undefined,
            incoming:
              | { cursor: string; hasMore: boolean; data: Reference[] }
              | undefined
          ) {
            let scans: {
              cursor: string;
              hasMore: boolean;
              data: Reference[];
            } = {
              cursor: '',
              hasMore: false,
              data: [],
            };

            if (existing) {
              scans = {
                cursor: existing.cursor,
                hasMore: existing.hasMore,
                data: [...scans.data, ...existing.data],
              };
            }

            if (incoming) {
              scans = {
                cursor: incoming.cursor,
                hasMore: incoming.hasMore,
                data: [
                  ...scans.data,
                  ...incoming.data.filter(
                    (a) =>
                      scans.data.find((scan) => scan.__ref === a.__ref) ===
                      undefined
                  ),
                ],
              };
            }
            return scans;
          },
        },
        listAlerts: {
          keyArgs: ['input', ['account_id', 'status', 'vitall_patient_id']],
        },
        getAllUsers: {
          keyArgs: ['input', ['account_id', 'roles']],
        },
        getQuestionnaire: {
          keyArgs: ['input', ['identifier', 'patient']],
        },
      },
    },
  },
};

export default config;
